import isEmpty from 'lodash/isEmpty';
import includes from 'lodash/includes';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {compose} from 'redux';
import moment from 'moment';
import {window} from 'ssr-window';
import DOMUtils from '@spothero/utils/dom';
import EnvironmentUtils from '@spothero/utils/environment';
import StorageUtils from '@spothero/utils/storage';
import ExperimentUtils from 'utils/experiment';
import GTMUtils from 'utils/gtm';
import SHUtils from 'utils/sh-utils';
import withConfig from 'config/withConfig';
import {getSpots} from 'spots/spots-selectors';
import {getTravelDistance, updateSelected} from 'spot/spot-actions';
import {setLocation} from 'search-request/search-request-actions';
import {goBack, push, replace} from 'router/router-actions';
import SegmentUtils from 'utils/segment';
import SpotUtils from 'utils/spot';
import {toggleFiltersView} from './search-actions';
import SearchMeta from './SearchMeta';
import {Geolocation} from 'search/mapbox/Geolocation';
import isNil from 'lodash/isNil';

const isBrowser = EnvironmentUtils.isBrowser();
const GeolocationMarker = isBrowser ? require('geolocation-marker') : null;

export default EnhancedComponent => {
    class WithSearch extends Component {
        constructor(props) {
            super(props);

            const {
                isMobile,
                searchType,
                destination,
                event,
                geolocationPositionMaximumAge,
            } = props;
            const isVenue = searchType === 'venue';
            const isEventListEnabled = Boolean(
                isVenue && isEmpty(event) && !destination.hide_event_modal
            );
            const featureName = 'mobile_desktop_polish';

            this._gmaps = isBrowser ? window.google.maps : null;
            this._geoMarker = isBrowser
                ? new GeolocationMarker.GeolocationMarker()
                : null;
            this._isGeolocationDisabled = false;
            this._isPolishEnabled = ExperimentUtils.isFeatureEnabled(
                featureName
            );
            this._isPolishTimepickersEnabled =
                ExperimentUtils.getFeatureVariable({
                    featureName,
                    variableName: 'timepickers',
                    type: 'Boolean',
                }) && !isMobile;

            if (isBrowser) {
                this._geoMarker.setPositionOptions({
                    maximumAge: geolocationPositionMaximumAge,
                });
            }

            this.state = {
                isEventListEnabled,
                showEventList: isMobile ? false : isEventListEnabled,
            };
        }

        static getDerivedStateFromProps(nextProps, prevState) {
            const {searchType, destination, event} = nextProps;

            if (searchType === 'venue' && !destination.hide_event_modal) {
                return {
                    isEventListEnabled: isEmpty(event),
                };
            } else if (
                searchType !== 'venue' ||
                !destination.has_upcoming_events ||
                destination.hide_event_modal
            ) {
                return {
                    isEventListEnabled: false,
                };
            }

            return null;
        }

        componentDidMount() {
            const {
                match: {
                    params: {page, section},
                },
                searchType,
                initialSpotId,
                referrer,
                referrerAffiliate,
            } = this.props;

            StorageUtils.set('searchStartTime', moment().format());

            this._searchPageContainer = DOMUtils.el('body');
            this._bodyClasses = SHUtils.determineInitialLoadBodyClasses({
                isSearchPage: true,
                searchType,
            });

            DOMUtils.addClass(this._searchPageContainer, this._bodyClasses);

            if (referrer || !includes(document.referrer, 'spothero.com')) {
                // only track this if the user is coming from an outside source not somewhere on our site
                SegmentUtils.track({
                    event: 'Search Landing',
                    properties: {
                        referrer: referrer || document.referrer,
                        'referrer affiliate': referrerAffiliate,
                    },
                });
            }

            this._gmaps.event.addListener(
                this._geoMarker,
                'geolocation_error',
                e => {
                    const searchControlsToggle = document.querySelector(
                        '.SearchDetails-destination'
                    );

                    // eslint-disable-next-line no-alert
                    alert(
                        'Sorry, your browser does not allow geolocation, please search an address.'
                    );

                    if (searchControlsToggle) {
                        searchControlsToggle.click();
                    }
                }
            );

            if (initialSpotId || (page === 'spot-details' && section)) {
                // a spot ID (`sid`) was passed in the URL or a user landed directly on spot-details, display the spot details with its information
                this._loadInitialSpotRates();
            }

            this._trackPageView();
        }

        componentDidUpdate(prevProps) {
            const {map, geolocated, searchType, searchRequestId} = this.props;
            const {
                searchType: prevSearchType,
                searchRequestId: prevSearchRequestId,
            } = prevProps;

            if (
                !geolocated &&
                map &&
                window.location.pathname.match(/parking-near-me/)
            ) {
                this._findParkingNearMe();
            }

            if (searchType !== prevSearchType) {
                DOMUtils.removeClass(
                    this._searchPageContainer,
                    this._bodyClasses
                );

                this._bodyClasses = SHUtils.determineInitialLoadBodyClasses({
                    isSearchPage: true,
                    searchType,
                });

                DOMUtils.addClass(this._searchPageContainer, this._bodyClasses);
            }

            if (searchRequestId !== prevSearchRequestId) {
                this._trackPageView();
            }
        }

        componentWillUnmount() {
            this._gmaps.event.clearListeners(
                this._geoMarker,
                'geolocation_error'
            );

            DOMUtils.removeClass(this._searchPageContainer, this._bodyClasses);

            if (this._rateLoader) {
                this._rateLoader.cancel();
                this._rateLoader = null;
            }
        }

        _onToggleEventList = () => {
            this.setState((prevState, props) => {
                return {
                    showEventList: !prevState.showEventList,
                };
            });
        };

        _onSpotDetailsHidden = () => {
            const {isMobile, routeBack, setSelectedSpot} = this.props;

            if (isMobile) {
                routeBack();
            } else {
                setSelectedSpot();
                this._resetToDefaultRoute();
            }
        };

        _trackPageView() {
            const {searchType} = this.props;

            GTMUtils.push({
                event: 'pageview',
                page: searchType === 'city' ? 'city' : 'search',
            });
        }

        _resetToDefaultRoute() {
            const {
                pageType,
                searchUrl,
                latitude,
                longitude,
                routeReplace,
            } = this.props;
            const url =
                pageType === 'address'
                    ? `/search?latitude=${latitude}&longitude=${longitude}`
                    : searchUrl;

            routeReplace(url);
        }

        _loadInitialSpotRates() {
            const {
                match: {
                    params: {section},
                },
                initialSpotId,
                isMonthly,
                starts,
                ends,
                timezone,
                eventId,
                setSelectedSpot,
                getSpotTravelDistance,
            } = this.props;

            this._rateLoader = SpotUtils.loadRates({
                spotId: initialSpotId || section,
                starts,
                ends,
                isMonthly,
                searchDataParams: {
                    monthly: isMonthly,
                    starts,
                    ends,
                    eid: eventId,
                },
                timezone,
                eventId,
                onSuccessWithRates: updatedSpotData => {
                    setSelectedSpot({
                        spot: updatedSpotData,
                        referrer: 'url',
                    });

                    getSpotTravelDistance(updatedSpotData).catch(error => {
                        // silently catch this error to ignore errors being logged
                    });

                    this._rateLoader = null;
                },
                onSuccessWithoutRates: () => {
                    this._rateLoader = null;
                    this._resetToDefaultRoute();
                },
                onError: error => {
                    this._rateLoader = null;
                    this._resetToDefaultRoute();
                },
            });
        }

        _findParkingNearMe = () => {
            const {setNewSearchLocation, map, isMapboxEnabled} = this.props;

            const setNewLocation = position => {
                if (isNil(position)) {
                    this._isGeolocationDisabled = true;

                    // eslint-disable-next-line no-alert
                    alert(
                        'There was an error determining your device location. ' +
                            'Please ensure your browser has location services enabled and try again.'
                    );

                    return;
                }

                const {latitude, longitude} = position;

                StorageUtils.set(
                    'sh-user-location',
                    {latitude, longitude},
                    'session'
                );

                setNewSearchLocation({
                    latitude,
                    longitude,
                    title: 'Current Location',
                    geolocated: true,
                });
            };

            if (this._isGeolocationDisabled) return;

            GTMUtils.push({
                event: 'GAEvent',
                eventCategory: 'button',
                eventAction: 'click',
                eventLabel: 'find parking near me',
                eventValue: undefined, // eslint-disable-line no-undefined
                hitCallback: undefined, // eslint-disable-line no-undefined
            });

            if (isMapboxEnabled) {
                Geolocation.getCurrentPosition()
                    .then(position => setNewLocation(position.coords))
                    .catch(() => setNewLocation(null));

                return;
            }

            this._geoMarker.setCircleOptions({fillColor: '#808080'});
            this._geoMarker.setMap(null);
            this._geoMarker.setMap(map);

            const gmaps = this._gmaps;
            const geoPositionListener = this._gmaps.event.addListener(
                this._geoMarker,
                'position_changed',
                // eslint-disable-next-line func-names
                function() {
                    // remove listener as it should only fire once per user request of location
                    gmaps.event.removeListener(geoPositionListener);

                    const position = this.getPosition();

                    setNewLocation(
                        position
                            ? {
                                  latitude: position.lat(),
                                  longitude: position.lng(),
                              }
                            : null
                    );
                }
            );
        };

        render() {
            const {
                searchType,
                searchUrl,
                isMonthly,
                spots,
                selectedSpot,
                routePush,
                idealDistance,
                isFiltered,
                onFiltersToggle,
            } = this.props;
            const {isEventListEnabled, showEventList} = this.state;

            return (
                <Fragment>
                    <SearchMeta />
                    <EnhancedComponent
                        searchType={searchType}
                        searchUrl={searchUrl}
                        isMonthly={isMonthly}
                        spots={spots}
                        selectedSpot={selectedSpot}
                        pushTo={routePush}
                        findParkingNearMe={this._findParkingNearMe}
                        onSpotDetailsHidden={this._onSpotDetailsHidden}
                        idealDistance={idealDistance}
                        isEventListEnabled={isEventListEnabled}
                        showEventList={showEventList}
                        isFiltered={isFiltered}
                        onFiltersToggle={onFiltersToggle}
                        onToggleEventList={this._onToggleEventList}
                        isPolishTimepickersEnabled={
                            this._isPolishTimepickersEnabled
                        }
                    />
                </Fragment>
            );
        }
    }

    WithSearch.propTypes = {
        match: PropTypes.shape({
            params: PropTypes.shape({
                page: PropTypes.string,
                section: PropTypes.string,
            }),
        }).isRequired,
        map: PropTypes.object,
        searchType: PropTypes.string.isRequired,
        isMobile: PropTypes.bool.isRequired,
        isMonthly: PropTypes.bool.isRequired,
        starts: PropTypes.string,
        ends: PropTypes.string,
        timezone: PropTypes.string.isRequired,
        eventId: PropTypes.number,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        event: PropTypes.object,
        spots: PropTypes.arrayOf(PropTypes.object).isRequired,
        selectedSpot: PropTypes.object,
        initialSpotId: PropTypes.number,
        searchRequestId: PropTypes.string,
        pageType: PropTypes.string,
        searchUrl: PropTypes.string,
        setSelectedSpot: PropTypes.func.isRequired,
        setNewSearchLocation: PropTypes.func.isRequired,
        idealDistance: PropTypes.number,
        geolocated: PropTypes.bool,
        isFiltered: PropTypes.bool,
        onFiltersToggle: PropTypes.func.isRequired,
        getSpotTravelDistance: PropTypes.func.isRequired,
        latitude: PropTypes.number.isRequired,
        longitude: PropTypes.number.isRequired,
        referrer: PropTypes.string,
        referrerAffiliate: PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.number,
        ]),
        geolocationPositionMaximumAge: PropTypes.number,
        routeBack: PropTypes.func.isRequired,
        routePush: PropTypes.func.isRequired,
        routeReplace: PropTypes.func.isRequired,
        isMapboxEnabled: PropTypes.bool,
    };

    const makeMapStateToProps = () => state => {
        const {
            city: {
                data: {timezone},
            },
            destination: {data: destination},
            event: {data: event},
            filters: {numActive},
            search: {
                data: {
                    searchType,
                    searchMapInstance: map,
                    geolocated,
                    sid: initialSpotId,
                    referrer,
                    referrerAffiliate,
                },
            },
            searchRequest: {
                monthly: isMonthly,
                starts,
                ends,
                latitude,
                longitude,
                eid: eventId,
                ideal_search_distance: idealDistance,
                id: searchRequestId,
                pageType,
                search_url: searchUrl,
            },
            spot: {selected: selectedSpot},
            user: {
                data: {experiments},
            },
        } = state;

        return {
            isMonthly,
            starts,
            ends,
            eventId,
            timezone,
            searchRequestId,
            pageType,
            searchUrl,
            destination,
            event,
            map,
            selectedSpot,
            searchType,
            idealDistance,
            geolocated,
            initialSpotId,
            latitude,
            longitude,
            referrer,
            referrerAffiliate,
            isFiltered: numActive > 0,
            spots: getSpots(state),
            isMapboxEnabled: experiments.mapbox_test === 'mapbox',
        };
    };

    const mapDispatchToProps = {
        setSelectedSpot: updateSelected,
        setNewSearchLocation: setLocation,
        onFiltersToggle: toggleFiltersView,
        getSpotTravelDistance: getTravelDistance,
        routeBack: goBack,
        routePush: push,
        routeReplace: replace,
    };

    const enhance = compose(
        withConfig(['isMobile']),
        withRouter,
        connect(
            makeMapStateToProps,
            mapDispatchToProps
        )
    );

    return enhance(WithSearch);
};
